/**
 * Shared tool schema definitions for spreadsheet operations
 * Supports both Excel and Google Sheets platforms
 *
 * These schemas are used by both client-side tools (tools.ts) and
 * server-side stub tools (tool-stubs.ts) to ensure consistency.
 */

import { z } from 'zod'

/**
 * Tool schema definitions - description, title, and inputSchema
 * The execute function is provided separately by client/server implementations
 */
export const toolSchemas = {
  getRangeSize: {
    title: 'Getting range size',
    description:
      'Get row and column dimensions of a cell range. Pure helper - does not read spreadsheet data.',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell range address (e.g., "A1:C10", "Sheet1!A1:B20")'),
    }),
  },

  readCellRanges: {
    title: 'Reading cells',
    description:
      'Read cell values from multiple ranges in one call. Returns a 2D array [[row1], [row2], ...] for each range.',
    inputSchema: z.object({
      addresses: z
        .array(z.string())
        .min(1)
        .max(50)
        .describe(
          'Array of cell range addresses to read. Example: ["A1:C10", "Sheet1!A1:B20", "E1:F5"]'
        ),
    }),
  },

  writeCellRanges: {
    title: 'Writing to cells',
    description:
      'Write values to one or more cell ranges. For formulas, use setFormulaRanges.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell range address (e.g., "A1:C10", "Sheet1!A1:B20")'),
            values: z
              .array(z.array(z.union([z.string(), z.number(), z.null()])))
              .describe('2D array of values [[row1_col1, row1_col2], ...]'),
            autofit: z
              .boolean()
              .optional()
              .default(true)
              .describe('Auto-fit columns and rows after writing'),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to write with their values'),
    }),
  },

  getSelectedRange: {
    title: 'Getting selected range',
    description:
      'Get the currently selected cell or range. Returns address and optionally values.',
    inputSchema: z.object({
      includeValues: z
        .boolean()
        .optional()
        .default(false)
        .describe('Include values from selected range'),
    }),
  },

  readSheetContext: {
    title: 'Reading sheet',
    description: 'Get active sheet info: used range, dimensions, sheet name.',
    inputSchema: z.object({
      detailed: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          'Include full used range values (use sparingly - only when you need all values and/or sheet is small)'
        ),
    }),
  },

  setFormulaRanges: {
    title: 'Setting formulas',
    description:
      'Set formula on one or more ranges. Provide formula for first cell only - relative refs auto-adjust.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe(
                'Cell range address (e.g., "D2:D100", "Sheet1!D2:D100")'
              ),
            formula: z
              .string()
              .describe(
                'Formula for first cell, starting with "=" (e.g., "=B2*2"). Relative refs auto-adjust; use $ for absolute refs.'
              ),
            autofit: z
              .boolean()
              .optional()
              .default(true)
              .describe('Auto-fit columns after setting formulas'),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to set formulas on'),
    }),
  },

  formatCells: {
    title: 'Formatting cells',
    description:
      'Apply formatting to one or more cell ranges. Only use when explicitly requested or essential to the task.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell range address (e.g., "A1:D10", "Sheet1!A1:D10")'),
            formatting: z.object({
              backgroundColor: z
                .string()
                .optional()
                .describe('Background color hex (e.g., "#E7E6E6")'),
              fontBold: z.boolean().optional().describe('Bold text'),
              fontItalic: z.boolean().optional().describe('Italic text'),
              fontColor: z
                .string()
                .optional()
                .describe('Font color hex (e.g., "#000000")'),
              fontSize: z.number().optional().describe('Font size in points'),
              numberFormat: z
                .string()
                .optional()
                .describe('Number format (e.g., "$#,##0.00", "mm/dd/yyyy")'),
              horizontalAlignment: z
                .enum(['Left', 'Center', 'Right', 'Justify'])
                .optional()
                .describe('Horizontal alignment'),
              verticalAlignment: z
                .enum(['Top', 'Center', 'Bottom'])
                .optional()
                .describe('Vertical alignment'),
              wrapText: z.boolean().optional().describe('Wrap text'),
            }),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to format with their formatting options'),
    }),
  },

  getCellFormat: {
    title: 'Reading cell format',
    description:
      'Get formatting information (background color, font, alignment, etc.) for one or more cell ranges.',
    inputSchema: z.object({
      addresses: z
        .array(
          z
            .string()
            .describe('Cell range address (e.g., "A1:D10", "Sheet1!A1:B20")')
        )
        .min(1)
        .max(50)
        .describe('Array of cell range addresses to read format from'),
    }),
  },

  clearCellRange: {
    title: 'Clearing cells',
    description:
      'Clear cell contents (values and formulas) from one or more ranges. Preserves formatting unless clearFormats=true.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell range address (e.g., "A1:C10", "Sheet1!A1:C10")'),
            clearFormats: z
              .boolean()
              .optional()
              .default(false)
              .describe('Also clear formatting'),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to clear'),
    }),
  },

  copyRange: {
    title: 'Copying range',
    description:
      'Copy a cell range to another location. Works across sheets via sheet prefixes.',
    inputSchema: z.object({
      sourceAddress: z
        .string()
        .describe('Source range (e.g., "A1:C10", "Sheet1!A1:C10")'),
      targetAddress: z
        .string()
        .describe('Target cell address (e.g., "E1", "Sheet2!E1")'),
      copyFormats: z
        .boolean()
        .optional()
        .default(true)
        .describe('Copy formatting along with values'),
    }),
  },

  getSheetDimensions: {
    title: 'Getting sheet dimensions',
    description:
      'Get active sheet dimensions (total rows/columns and optionally used range).',
    inputSchema: z.object({
      includeUsedRange: z
        .boolean()
        .optional()
        .default(true)
        .describe('Include used range address'),
    }),
  },

  insertRows: {
    title: 'Inserting rows',
    description:
      'Insert rows at specified position. Existing rows shift down by default.',
    inputSchema: z.object({
      startRow: z
        .number()
        .describe('Row index (1-based) to insert at (e.g., 5)'),
      count: z
        .number()
        .optional()
        .default(1)
        .describe('Number of rows to insert'),
      shiftDown: z
        .boolean()
        .optional()
        .default(true)
        .describe('Shift existing rows down'),
    }),
  },

  insertColumns: {
    title: 'Inserting columns',
    description:
      'Insert columns at specified position. Existing columns shift right by default.',
    inputSchema: z.object({
      startColumn: z
        .number()
        .describe('Column index (1-based) to insert at (e.g., 3 for column C)'),
      count: z
        .number()
        .optional()
        .default(1)
        .describe('Number of columns to insert'),
      shiftRight: z
        .boolean()
        .optional()
        .default(true)
        .describe('Shift existing columns right'),
    }),
  },

  deleteRows: {
    title: 'Deleting rows',
    description:
      'Delete rows from active sheet. Remaining rows shift up by default.',
    inputSchema: z.object({
      startRow: z
        .number()
        .describe('Row index (1-based) of first row to delete'),
      count: z
        .number()
        .optional()
        .default(1)
        .describe('Number of rows to delete'),
      shiftUp: z
        .boolean()
        .optional()
        .default(true)
        .describe('Shift remaining rows up'),
    }),
  },

  deleteColumns: {
    title: 'Deleting columns',
    description:
      'Delete columns from active sheet. Remaining columns shift left by default.',
    inputSchema: z.object({
      startColumn: z
        .number()
        .describe('Column index (1-based) of first column to delete'),
      count: z
        .number()
        .optional()
        .default(1)
        .describe('Number of columns to delete'),
      shiftLeft: z
        .boolean()
        .optional()
        .default(true)
        .describe('Shift remaining columns left'),
    }),
  },

  createTable: {
    title: 'Creating table',
    description: 'Convert a cell range to a table with headers.',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell range to convert (e.g., "A1:D10", "Sheet1!A1:D10")'),
      hasHeaders: z
        .boolean()
        .optional()
        .default(true)
        .describe('First row contains headers'),
      tableName: z.string().optional().describe('Table name'),
    }),
  },

  sortRange: {
    title: 'Sorting values',
    description:
      'Sort data in a range by column. Supports ascending/descending order.',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell range to sort (e.g., "A1:D10", "Sheet1!A1:D10")'),
      key: z
        .number()
        .describe('Column index (1-based) to sort by (e.g., 1 for column A)'),
      ascending: z
        .boolean()
        .optional()
        .default(true)
        .describe('Ascending (true) or descending (false)'),
      hasHeaders: z
        .boolean()
        .optional()
        .default(true)
        .describe('First row contains headers (excluded from sort)'),
    }),
  },

  findAndReplace: {
    title: 'Finding and replacing text',
    description:
      'Find and replace text in a cell range. Supports case-sensitive and exact cell matching.',
    inputSchema: z.object({
      address: z
        .string()
        .optional()
        .describe(
          'Cell range to search (e.g., "A1:D10"). If omitted, searches entire active sheet'
        ),
      findText: z.string().describe('Text to find'),
      replaceText: z.string().describe('Text to replace with'),
      matchCase: z
        .boolean()
        .optional()
        .default(false)
        .describe('Case-sensitive matching'),
      matchEntireCell: z
        .boolean()
        .optional()
        .default(false)
        .describe('Match entire cell contents only'),
    }),
  },

  mergeCells: {
    title: 'Merging cells',
    description:
      'Merge cells in one or more ranges into single cells. Top-left cell value is preserved.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell range address (e.g., "A1:C1", "Sheet1!A1:C1")'),
            across: z
              .boolean()
              .optional()
              .default(false)
              .describe(
                'Merge across rows only (Excel: "Merge Across", Sheets: horizontal merge)'
              ),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to merge'),
    }),
  },

  unmergeCells: {
    title: 'Unmerging cells',
    description: 'Unmerge previously merged cells in one or more ranges.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell range address (e.g., "A1:C1", "Sheet1!A1:C1")'),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to unmerge'),
    }),
  },

  addHyperlink: {
    title: 'Adding hyperlink',
    description: 'Add hyperlink(s) to one or more cells.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell address (e.g., "A1", "Sheet1!A1")'),
            url: z.string().describe('URL for the hyperlink'),
            displayText: z
              .string()
              .optional()
              .describe('Text to display (defaults to URL)'),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of hyperlinks to add'),
    }),
  },

  listWorksheets: {
    title: 'Listing sheets',
    description: 'Get list of all sheets with names and visibility status.',
    inputSchema: z.object({}),
  },

  switchWorksheet: {
    title: 'Switching worksheet',
    description:
      'Switch active sheet by name. Use listWorksheets to see available sheets.',
    inputSchema: z.object({
      sheetName: z.string().describe('Sheet name to activate'),
    }),
  },

  createWorksheet: {
    title: 'Creating worksheet',
    description:
      'Create one or more new sheets. The last created sheet becomes active.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            sheetName: z.string().describe('Name for the new sheet'),
            position: z
              .number()
              .optional()
              .describe('Position index (0-based). If omitted, adds at end.'),
          })
        )
        .describe('Array of worksheets to create'),
    }),
  },

  createChart: {
    title: 'Creating chart',
    description:
      'Create a chart from a data range. Chart types are Excel-compatible; Sheets maps to equivalents.',
    inputSchema: z.object({
      dataRange: z
        .string()
        .describe(
          'Cell range with data (e.g., "A1:D10", "Sheet1!A1:D10"). First row = headers if hasHeaders=true.'
        ),
      chartType: z
        .enum([
          'ColumnClustered',
          'ColumnStacked',
          'ColumnStacked100',
          'Line',
          'LineStacked',
          'LineStacked100',
          'Pie',
          'BarClustered',
          'BarStacked',
          'BarStacked100',
          'Area',
          'AreaStacked',
          'AreaStacked100',
          'Scatter',
          'Doughnut',
        ])
        .describe('Chart type'),
      hasHeaders: z
        .boolean()
        .optional()
        .default(true)
        .describe('First row contains headers'),
      title: z.string().optional().describe('Chart title'),
      position: z
        .string()
        .optional()
        .describe(
          'Cell address to position chart (e.g., "F1"). If omitted, placed below data.'
        ),
      width: z
        .number()
        .optional()
        .describe('Chart width in points (default: 400)'),
      height: z
        .number()
        .optional()
        .describe('Chart height in points (default: 300)'),
    }),
  },

  fetchDataFromUrl: {
    title: 'Fetching data from URL',
    description:
      'Fetch data from a URL. Supports JSON, CSV, HTML, images, and text. Returns structured data for JSON/CSV, or raw content for images/text. Use to retrieve data from web APIs, download files, or access images.',
    inputSchema: z.object({
      url: z.string().describe('URL to fetch data from'),
      format: z
        .enum(['auto', 'json', 'csv', 'text', 'image'])
        .optional()
        .default('auto')
        .describe(
          'Expected format. "auto" detects from Content-Type header. Use "image" for images (returns base64 data URL).'
        ),
    }),
  },

  // Excel-specific tools
  setDataValidation: {
    title: 'Setting data validation',
    description:
      'Set data validation rules on a range (Excel only). Restricts what values can be entered in cells.',
    inputSchema: z.object({
      address: z
        .string()
        .describe(
          'Cell range to apply validation (e.g., "A1:A10", "Sheet1!A1:A10")'
        ),
      rule: z.object({
        type: z
          .enum([
            'WholeNumber',
            'Decimal',
            'TextLength',
            'Date',
            'Time',
            'List',
            'Custom',
          ])
          .describe('Validation type'),
        operator: z
          .enum([
            'Between',
            'NotBetween',
            'Equal',
            'NotEqual',
            'GreaterThan',
            'LessThan',
            'GreaterThanOrEqual',
            'LessThanOrEqual',
          ])
          .optional()
          .describe('Comparison operator (not used for List type)'),
        formula1: z
          .string()
          .optional()
          .describe('First formula/value for validation'),
        formula2: z
          .string()
          .optional()
          .describe('Second formula/value (for Between operators)'),
        showInputMessage: z
          .boolean()
          .optional()
          .default(true)
          .describe('Show input message when cell is selected'),
        inputTitle: z.string().optional().describe('Title for input message'),
        inputMessage: z.string().optional().describe('Input message text'),
        showErrorAlert: z
          .boolean()
          .optional()
          .default(true)
          .describe('Show error alert when validation fails'),
        errorStyle: z
          .enum(['Stop', 'Warning', 'Information'])
          .optional()
          .default('Stop')
          .describe('Error alert style'),
        errorTitle: z.string().optional().describe('Title for error alert'),
        errorMessage: z.string().optional().describe('Error message text'),
      }),
    }),
  },

  getComments: {
    title: 'Reading comments',
    description:
      'Get all comments from one or more ranges (Excel only). If address is empty array, gets all comments from active sheet.',
    inputSchema: z.object({
      address: z
        .array(z.string())
        .max(50)
        .optional()
        .describe(
          'Cell range(s) to get comments from. If omitted or empty, gets all comments from active sheet.'
        ),
    }),
  },

  freezePanes: {
    title: 'Freezing panes',
    description:
      'Freeze rows and/or columns at a specified position. Use "A1" to unfreeze.',
    inputSchema: z.object({
      address: z
        .string()
        .describe(
          'Cell address where freeze should occur (e.g., "B2" freezes row 1 and column A). Use "A1" to unfreeze.'
        ),
    }),
  },

  getTableRows: {
    title: 'Reading table',
    description: 'Get rows from an Excel table by table name (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
      includeHeaders: z
        .boolean()
        .optional()
        .default(false)
        .describe('Include header row in results'),
    }),
  },

  addTableRow: {
    title: 'Adding table row',
    description: 'Add a row to an Excel table (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
      values: z
        .array(z.union([z.string(), z.number(), z.null()]))
        .describe('Array of values for the new row'),
    }),
  },

  executeCodeExcel: {
    title: 'Executing code',
    description:
      'Execute JavaScript code. Use Excel.run() to access Excel APIs. Useful for complex operations that require custom logic or multiple Excel API calls. PREFER FORMULAS TO EXECUTE CODE, FOR USER TRACEABILITY',
    inputSchema: z.object({
      code: z
        .string()
        .describe(
          'JavaScript code (not TypeScript - no type annotations). Write code directly - do not wrap in a function definition. The code will be executed automatically. Start with "await Excel.run(async (context) => { ... });". Return value to include in result. Fetch data (e.g. sheet.getRange("J1").load("values")) instead of hardcoding.'
        ),
    }),
  },

  // Sheets-specific tools
  createNamedRange: {
    title: 'Creating named range',
    description: 'Create a named range.',
    inputSchema: z.object({
      name: z.string().describe('Name for the range'),
      address: z
        .string()
        .describe('Cell range address (e.g., "A1:B10", "Sheet1!A1:B10")'),
    }),
  },

  getNamedRanges: {
    title: 'Getting named ranges',
    description: 'Get all named ranges in the spreadsheet.',
    inputSchema: z.object({}),
  },

  setFilter: {
    title: 'Setting filter',
    description: 'Create or update a filter on a range.',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell range to filter (e.g., "A1:D10", "Sheet1!A1:D10")'),
      columnIndex: z
        .number()
        .optional()
        .describe(
          'Column index (1-based) to filter by. If omitted, creates filter on entire range.'
        ),
      criteria: z
        .object({
          hiddenValues: z
            .array(z.string())
            .optional()
            .describe('Values to hide'),
          condition: z
            .enum([
              'NUMBER_GREATER',
              'NUMBER_GREATER_THAN_EQ',
              'NUMBER_LESS',
              'NUMBER_LESS_THAN_EQ',
              'NUMBER_EQ',
              'NUMBER_NOT_EQ',
              'NUMBER_BETWEEN',
              'NUMBER_NOT_BETWEEN',
              'TEXT_CONTAINS',
              'TEXT_NOT_CONTAINS',
              'TEXT_STARTS_WITH',
              'TEXT_ENDS_WITH',
              'TEXT_EQ',
              'TEXT_IS_EMPTY',
              'TEXT_IS_NOT_EMPTY',
              'DATE_EQ',
              'DATE_BEFORE',
              'DATE_AFTER',
              'DATE_ON_OR_BEFORE',
              'DATE_ON_OR_AFTER',
              'DATE_BETWEEN',
              'DATE_NOT_BETWEEN',
            ])
            .optional()
            .describe('Filter condition type'),
          values: z
            .array(z.union([z.string(), z.number()]))
            .optional()
            .describe('Values for the condition'),
        })
        .optional()
        .describe('Filter criteria'),
    }),
  },

  removeFilter: {
    title: 'Removing filter',
    description: 'Remove filter from a range.',
    inputSchema: z.object({
      address: z
        .string()
        .describe(
          'Cell range with filter to remove (e.g., "A1:D10", "Sheet1!A1:D10")'
        ),
    }),
  },

  addNote: {
    title: 'Adding notes',
    description:
      'Add note(s) to one or more cells (Sheets only). Notes are different from comments.',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell address (e.g., "A1", "Sheet1!A1")'),
            note: z.string().describe('Note text'),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of notes to add'),
    }),
  },

  getNotes: {
    title: 'Getting notes',
    description: 'Get all notes from a range (Sheets only).',
    inputSchema: z.object({
      address: z
        .string()
        .optional()
        .describe(
          'Cell range to get notes from. If omitted, gets all notes from active sheet.'
        ),
    }),
  },

  setDataValidationSheets: {
    title: 'Setting data validation',
    description:
      'Apply validation rules to cells (dropdowns, number ranges, date constraints, text validation, custom formulas). Use for data quality and user input control. (Sheets only)',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe(
                'Cell range address (e.g., "A2:A100", "Sheet1!B2:B50")'
              ),
            validation: z.object({
              type: z
                .enum([
                  'LIST',
                  'LIST_FROM_RANGE',
                  'NUMBER_GREATER',
                  'NUMBER_LESS',
                  'NUMBER_BETWEEN',
                  'NUMBER_EQUAL',
                  'DATE_AFTER',
                  'DATE_BEFORE',
                  'DATE_BETWEEN',
                  'TEXT_CONTAINS',
                  'TEXT_LENGTH',
                  'CHECKBOX',
                  'CUSTOM_FORMULA',
                ])
                .describe('Type of validation rule'),
              values: z
                .array(z.union([z.string(), z.number()]))
                .optional()
                .describe(
                  'Values for validation: list items ["Yes","No"], number range [100,200], date strings ["2024-01-01"], formula ["=A1>0"]'
                ),
              allowInvalid: z
                .boolean()
                .optional()
                .default(false)
                .describe(
                  'Allow invalid values (show warning but allow entry)'
                ),
              showDropdown: z
                .boolean()
                .optional()
                .default(true)
                .describe('Show dropdown for LIST types'),
              helpText: z
                .string()
                .optional()
                .describe('Tooltip text shown when cell is selected'),
            }),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to apply validation rules'),
    }),
  },

  clearDataValidationSheets: {
    title: 'Clearing data validation',
    description:
      'Remove validation rules from cell ranges. Cells return to accepting any value. (Sheets only)',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe(
                'Cell range address (e.g., "A2:A100", "Sheet1!B2:B50")'
              ),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to clear validation from'),
    }),
  },

  getDataValidationSheets: {
    title: 'Getting data validation',
    description:
      'Read validation rules from a cell range. Returns validation type and configuration. (Sheets only)',
    inputSchema: z.object({
      address: z
        .string()
        .describe(
          'Cell range address to read validation from (e.g., "A2:A100")'
        ),
    }),
  },

  addConditionalFormatRuleSheets: {
    title: 'Adding conditional format rule',
    description:
      'Apply conditional formatting rules to highlight cells based on values (color scales, data bars, condition-based formatting). Use for visual data analysis. (Sheets only)',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe(
                'Cell range address (e.g., "A1:C100", "Sheet1!B2:E50")'
              ),
            rule: z.union([
              // Cell value condition rules (use ruleType as the condition directly)
              z.object({
                ruleType: z
                  .enum([
                    'GREATER_THAN',
                    'LESS_THAN',
                    'GREATER_THAN_OR_EQUAL',
                    'LESS_THAN_OR_EQUAL',
                    'BETWEEN',
                    'NOT_BETWEEN',
                    'EQUAL_TO',
                    'NOT_EQUAL_TO',
                    'TEXT_CONTAINS',
                    'TEXT_DOES_NOT_CONTAIN',
                    'TEXT_STARTS_WITH',
                    'TEXT_ENDS_WITH',
                    'TEXT_EQUAL_TO',
                    'TEXT_NOT_EQUAL_TO',
                    'DATE_AFTER',
                    'DATE_BEFORE',
                    'DATE_EQUAL_TO',
                    'CELL_EMPTY',
                    'CELL_NOT_EMPTY',
                  ])
                  .describe('The condition type for cell value rules'),
                values: z
                  .array(z.union([z.string(), z.number()]))
                  .describe(
                    'Values for condition: single value [100], range [0,100], text ["error"]'
                  ),
                format: z.object({
                  backgroundColor: z
                    .string()
                    .optional()
                    .describe('Background color hex (e.g., "#FFFF00")'),
                  fontColor: z
                    .string()
                    .optional()
                    .describe('Font color hex (e.g., "#FF0000")'),
                  bold: z.boolean().optional().describe('Bold text'),
                  italic: z.boolean().optional().describe('Italic text'),
                  strikethrough: z
                    .boolean()
                    .optional()
                    .describe('Strikethrough text'),
                  underline: z.boolean().optional().describe('Underline text'),
                }),
              }),
              // Gradient/color scale rules
              z.object({
                ruleType: z.literal('GRADIENT'),
                minColor: z
                  .string()
                  .describe(
                    'Color for minimum value (e.g., "#FF0000" for red)'
                  ),
                midColor: z
                  .string()
                  .optional()
                  .describe(
                    'Color for midpoint value (e.g., "#FFFF00" for yellow)'
                  ),
                maxColor: z
                  .string()
                  .describe(
                    'Color for maximum value (e.g., "#00FF00" for green)'
                  ),
                minType: z
                  .enum(['NUMBER', 'PERCENT', 'PERCENTILE'])
                  .optional()
                  .describe('How to interpret minValue'),
                minValue: z
                  .string()
                  .optional()
                  .describe('Specific minimum value (omit for data min)'),
                midType: z
                  .enum(['NUMBER', 'PERCENT', 'PERCENTILE'])
                  .optional()
                  .describe('How to interpret midValue'),
                midValue: z
                  .string()
                  .optional()
                  .describe('Specific midpoint value'),
                maxType: z
                  .enum(['NUMBER', 'PERCENT', 'PERCENTILE'])
                  .optional()
                  .describe('How to interpret maxValue'),
                maxValue: z
                  .string()
                  .optional()
                  .describe('Specific maximum value (omit for data max)'),
              }),
              // Custom formula rules
              z.object({
                ruleType: z.literal('CUSTOM_FORMULA'),
                formula: z
                  .string()
                  .describe(
                    'Formula that returns true/false (e.g., "=MOD(ROW(),2)=0" for even rows)'
                  ),
                format: z.object({
                  backgroundColor: z.string().optional(),
                  fontColor: z.string().optional(),
                  bold: z.boolean().optional(),
                  italic: z.boolean().optional(),
                  strikethrough: z.boolean().optional(),
                  underline: z.boolean().optional(),
                }),
              }),
            ]),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to apply conditional formatting rules'),
    }),
  },

  clearConditionalFormatRulesSheets: {
    title: 'Clearing conditional format rules',
    description:
      'Remove all conditional formatting rules from cell ranges. Cells return to default formatting. (Sheets only)',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe(
                'Cell range address (e.g., "A1:C100", "Sheet1!B2:E50")'
              ),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to clear conditional formatting from'),
    }),
  },

  getConditionalFormatRulesSheets: {
    title: 'Getting conditional format rules',
    description:
      'Read all conditional formatting rules for the active sheet. Returns rule configurations. (Sheets only)',
    inputSchema: z.object({}),
  },

  setCellBordersSheets: {
    title: 'Setting cell borders',
    description:
      'Apply borders to cells (top, bottom, left, right, internal vertical/horizontal lines). Use for table formatting and visual organization. (Sheets only)',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell range address (e.g., "A1:D10", "Sheet1!B2:E20")'),
            borders: z.object({
              top: z.boolean().optional().describe('Top border'),
              bottom: z.boolean().optional().describe('Bottom border'),
              left: z.boolean().optional().describe('Left border'),
              right: z.boolean().optional().describe('Right border'),
              vertical: z
                .boolean()
                .optional()
                .describe('Internal vertical lines between columns'),
              horizontal: z
                .boolean()
                .optional()
                .describe('Internal horizontal lines between rows'),
              color: z
                .string()
                .optional()
                .default('#000000')
                .describe('Border color hex (e.g., "#000000" for black)'),
              style: z
                .enum([
                  'SOLID',
                  'SOLID_MEDIUM',
                  'SOLID_THICK',
                  'DASHED',
                  'DOTTED',
                  'DOUBLE',
                ])
                .optional()
                .default('SOLID')
                .describe('Border style'),
            }),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to apply borders'),
    }),
  },

  clearCellBordersSheets: {
    title: 'Clearing cell borders',
    description: 'Remove all borders from cell ranges. (Sheets only)',
    inputSchema: z.object({
      items: z
        .array(
          z.object({
            address: z
              .string()
              .describe('Cell range address (e.g., "A1:D10", "Sheet1!B2:E20")'),
          })
        )
        .min(1)
        .max(50)
        .describe('Array of ranges to clear borders from'),
    }),
  },

  executeCodeSheets: {
    title: 'Executing code',
    description:
      'Execute JavaScript code in the Apps Script context. SpreadsheetApp and other Google Apps Script APIs are available. Use for complex custom operations. Write code directly - do not wrap in a function definition. The code will be executed automatically. Return a value to include in result.',
    inputSchema: z.object({
      code: z
        .string()
        .describe(
          'JavaScript code to execute directly. SpreadsheetApp, Session, Utilities, and other Apps Script APIs are available. Send changes to the grid using methods like .setValues(), .setFormula(), or .clearContent(). Fetch data (e.g. sheet.getRange("D1").getValue())instead of hardcoding'
        ),
    }),
  },

  listConnectors: {
    title: 'Listing available connectors',
    description:
      'List all available connectors (databases, email, etc.). Returns connector IDs that can be used with queryDatabase or sendEmail.',
    inputSchema: z.object({}),
  },

  queryDatabase: {
    title: 'Querying database',
    description:
      'Execute a SQL query against a database using a connector ID from listConnectors.',
    inputSchema: z.object({
      connectorId: z.string().describe('Connector ID from listConnectors'),
      query: z
        .string()
        .describe('SQL query to execute (SELECT queries only for safety)'),
    }),
  },

  queryDatabaseToSheet: {
    title: 'Querying database to sheet',
    description:
      'Execute a SQL query and write results directly to a cell range. More efficient than queryDatabase + writeCellRanges for large result sets since data is written without streaming back to AI.',
    inputSchema: z.object({
      connectorId: z.string().describe('Connector ID from listConnectors'),
      query: z
        .string()
        .describe('SQL query to execute (SELECT queries only for safety)'),
      targetAddress: z
        .string()
        .describe(
          'Cell address to write results (e.g., "A1", "Sheet1!A1"). Results expand from this cell.'
        ),
      includeHeaders: z
        .boolean()
        .optional()
        .default(true)
        .describe('Include column names as header row'),
      autofit: z
        .boolean()
        .optional()
        .default(true)
        .describe('Auto-fit columns after writing'),
    }),
  },

  sendEmail: {
    title: 'Sending email via Resend',
    description:
      'Send an email using a Resend connector ID from listConnectors. NOT for Gmail — use sendGmailMessage for Gmail. The "from" email and "replyTo" can be provided in params or configured in the connector. Leave "from" and "replyTo" blank if not provided, we will use the connector config values.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Resend connector ID from listConnectors'),
      to: z
        .string()
        .describe(
          'Recipient email address (or comma-separated list for multiple recipients)'
        ),
      subject: z.string().describe('Email subject'),
      body: z.string().describe('Email body (HTML supported)'),
      from: z
        .string()
        .optional()
        .describe(
          'Sender email address (optional if configured in connector as from). Must be a verified domain in Resend.'
        ),
      replyTo: z
        .string()
        .optional()
        .describe(
          'Reply-to email address (optional if configured in connector as replyTo)'
        ),
    }),
  },

  sendSlackMessage: {
    title: 'Sending Slack message',
    description:
      'Send a message to a Slack channel using a Slack connector ID from listConnectors. The "channel" can be provided in params or configured in the connector. Leave "channel" blank if not provided, we will use the connector config value.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Slack connector ID from listConnectors'),
      channel: z
        .string()
        .optional()
        .describe(
          'Channel name or ID (e.g., "#general" or "C1234567890"). Optional if configured in connector config.'
        ),
      message: z.string().describe('Message text to send to Slack'),
    }),
  },

  queryStripeCustomers: {
    title: 'Querying Stripe customers',
    description:
      'List customers from a Stripe account using a Stripe connector ID from listConnectors. Returns customer details including name, email, and balance.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Stripe connector ID from listConnectors'),
      limit: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of customers to return (1-100, default: 25)'),
      email: z
        .string()
        .optional()
        .describe('Filter by exact customer email address'),
    }),
  },

  queryStripePayments: {
    title: 'Querying Stripe payments',
    description:
      'List payment intents from a Stripe account using a Stripe connector ID from listConnectors. Returns payment details including amount, status, and customer.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Stripe connector ID from listConnectors'),
      limit: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of payments to return (1-100, default: 25)'),
      customer: z
        .string()
        .optional()
        .describe('Filter by customer ID (e.g., "cus_...")'),
    }),
  },

  queryStripeInvoices: {
    title: 'Querying Stripe invoices',
    description:
      'List invoices from a Stripe account using a Stripe connector ID from listConnectors. Returns invoice details including amounts, status, and customer info.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Stripe connector ID from listConnectors'),
      limit: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of invoices to return (1-100, default: 25)'),
      customer: z
        .string()
        .optional()
        .describe('Filter by customer ID (e.g., "cus_...")'),
      status: z
        .enum(['draft', 'open', 'paid', 'uncollectible', 'void'])
        .optional()
        .describe('Filter by invoice status'),
    }),
  },

  queryStripeSubscriptions: {
    title: 'Querying Stripe subscriptions',
    description:
      'List subscriptions from a Stripe account using a Stripe connector ID from listConnectors. Returns subscription details including status, plan, and billing period.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Stripe connector ID from listConnectors'),
      limit: z
        .number()
        .optional()
        .default(25)
        .describe(
          'Maximum number of subscriptions to return (1-100, default: 25)'
        ),
      customer: z
        .string()
        .optional()
        .describe('Filter by customer ID (e.g., "cus_...")'),
      status: z
        .enum([
          'active',
          'past_due',
          'unpaid',
          'canceled',
          'incomplete',
          'incomplete_expired',
          'trialing',
        ])
        .optional()
        .describe('Filter by subscription status'),
    }),
  },

  getStripeBalance: {
    title: 'Getting Stripe balance',
    description:
      'Get the current balance from a Stripe account using a Stripe connector ID from listConnectors. Returns available and pending amounts per currency.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Stripe connector ID from listConnectors'),
    }),
  },

  queryStripeProducts: {
    title: 'Querying Stripe products',
    description:
      'List products from a Stripe account using a Stripe connector ID from listConnectors. Returns product details including name, description, and pricing.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Stripe connector ID from listConnectors'),
      limit: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of products to return (1-100, default: 25)'),
      active: z
        .boolean()
        .optional()
        .describe('Filter by active status (true for active products only)'),
    }),
  },

  queryStripePayouts: {
    title: 'Querying Stripe payouts',
    description:
      'List payouts from a Stripe account using a Stripe connector ID from listConnectors. Returns payout details including amount, status, and arrival date.',
    inputSchema: z.object({
      connectorId: z
        .string()
        .describe('Stripe connector ID from listConnectors'),
      limit: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of payouts to return (1-100, default: 25)'),
      status: z
        .enum(['paid', 'pending', 'in_transit', 'canceled', 'failed'])
        .optional()
        .describe('Filter by payout status'),
    }),
  },

  webSearch: {
    title: 'Searching the web',
    description:
      'Search the web for current information using Google. Use when you need real-time data, current events, or information not available in the spreadsheet.',
    inputSchema: z.object({
      query: z
        .string()
        .describe('Search query (e.g., "current USD to EUR exchange rate")'),
      numResults: z
        .number()
        .optional()
        .default(5)
        .describe('Number of results to return (1-10, default: 5)'),
    }),
  },

  // Excel-specific advanced tools
  autoFill: {
    title: 'Auto-filling range',
    description:
      'Auto-fill a range with series, dates, or trends based on a source pattern. Common for extending number sequences, dates, or formulas.',
    inputSchema: z.object({
      sourceAddress: z
        .string()
        .describe(
          'Source range with the pattern to fill (e.g., "A1:A3" with values 1, 2, 3)'
        ),
      destinationAddress: z
        .string()
        .describe(
          'Destination range to fill (e.g., "A1:A10" to extend the pattern)'
        ),
      autoFillType: z
        .enum([
          'FillDefault',
          'FillCopy',
          'FillSeries',
          'FillFormats',
          'FillValues',
          'FillDays',
          'FillWeekdays',
          'FillMonths',
          'FillYears',
          'LinearTrend',
          'GrowthTrend',
          'FlashFill',
        ])
        .optional()
        .default('FillDefault')
        .describe(
          'Type of auto-fill: FillDefault (Excel decides), FillSeries (extend series), FillDays/Months/Years (date series), LinearTrend/GrowthTrend (trends), etc.'
        ),
    }),
  },

  removeDuplicates: {
    title: 'Removing duplicates',
    description:
      'Remove duplicate rows from a range based on specified columns. Returns count of removed and remaining rows.',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Range address to remove duplicates from (e.g., "A1:D100")'),
      columns: z
        .array(z.number())
        .describe(
          'Column indices (1-based) to check for duplicates. Example: [1, 2] checks columns A and B. Empty array checks all columns.'
        ),
      hasHeaders: z
        .boolean()
        .optional()
        .default(true)
        .describe('First row contains headers (excluded from duplicate check)'),
    }),
  },

  deleteWorksheet: {
    title: 'Deleting worksheet',
    description: 'Delete a worksheet/sheet from the workbook.',
    inputSchema: z.object({
      sheetName: z.string().describe('Name of the worksheet to delete'),
    }),
  },

  renameWorksheet: {
    title: 'Renaming worksheet',
    description: 'Rename a worksheet.',
    inputSchema: z.object({
      oldName: z.string().describe('Current worksheet name'),
      newName: z.string().describe('New worksheet name'),
    }),
  },

  getSpecialCells: {
    title: 'Finding special cells',
    description:
      'Find cells matching specific criteria (blanks, formulas, constants, errors, etc.) in a range. Returns addresses of matching cells. (Excel only)',
    inputSchema: z.object({
      address: z
        .string()
        .describe(
          'Range address to search (e.g., "A1:D100", "Sheet1!A1:D100")'
        ),
      cellType: z
        .enum([
          'Blanks',
          'Constants',
          'Formulas',
          'ConditionalFormats',
          'DataValidations',
          'SameConditionalFormat',
          'SameDataValidation',
          'Visible',
        ])
        .describe(
          'Type of cells to find: Blanks (empty cells), Constants (non-formula values), Formulas, Errors, ConditionalFormats, DataValidations, etc.'
        ),
      cellValueType: z
        .enum([
          'All',
          'Errors',
          'ErrorsLogical',
          'ErrorsNumbers',
          'ErrorsText',
          'ErrorsLogicalNumber',
          'ErrorsLogicalText',
          'ErrorsNumberText',
          'Logical',
          'LogicalNumbers',
          'LogicalText',
          'LogicalNumbersText',
          'Numbers',
          'NumbersText',
          'Text',
        ])
        .optional()
        .describe(
          'Value type filter (used with Constants/Formulas): All, Errors, Numbers, Text, Logical, etc.'
        ),
    }),
  },

  addComment: {
    title: 'Adding comment',
    description: 'Add a comment to a cell (Excel only).',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell address to add comment to (e.g., "A1", "Sheet1!B5")'),
      content: z.string().describe('Comment text content'),
    }),
  },

  deleteTable: {
    title: 'Deleting table',
    description: 'Delete an Excel table (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table to delete'),
    }),
  },

  addTableColumn: {
    title: 'Adding table column',
    description: 'Add a column to an Excel table (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
      columnName: z.string().optional().describe('Name for the new column'),
      index: z
        .number()
        .optional()
        .describe(
          'Index position to insert at (0-based). If omitted, adds at end.'
        ),
      values: z
        .array(
          z.array(z.union([z.string(), z.number(), z.boolean(), z.null()]))
        )
        .optional()
        .describe(
          '2D array of values for the column. If omitted, column is empty.'
        ),
    }),
  },

  deleteTableColumn: {
    title: 'Deleting table column',
    description: 'Delete a column from an Excel table (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
      columnName: z
        .string()
        .optional()
        .describe('Name of the column to delete (if provided)'),
      columnIndex: z
        .number()
        .optional()
        .describe(
          'Index of the column to delete (0-based). Use either columnName or columnIndex.'
        ),
    }),
  },

  // Table operations
  resizeTable: {
    title: 'Resizing table',
    description: 'Resize an Excel table to a new range (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
      newRange: z
        .string()
        .describe('New range address for the table (e.g., "A1:D100")'),
    }),
  },

  convertTableToRange: {
    title: 'Converting table to range',
    description: 'Convert an Excel table back to a regular range (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table to convert'),
    }),
  },

  clearTableFilters: {
    title: 'Clearing table filters',
    description: 'Clear all filters from an Excel table (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
    }),
  },

  reapplyTableFilters: {
    title: 'Reapplying table filters',
    description: 'Reapply filters to an Excel table (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
    }),
  },

  listTableColumns: {
    title: 'Listing table columns',
    description: 'List all columns in an Excel table (Excel only).',
    inputSchema: z.object({
      tableName: z.string().describe('Name of the table'),
    }),
  },

  // Chart operations
  deleteChart: {
    title: 'Deleting chart',
    description: 'Delete a chart from a worksheet (Excel only).',
    inputSchema: z.object({
      chartName: z.string().describe('Name of the chart to delete'),
    }),
  },

  updateChartData: {
    title: 'Updating chart data',
    description: 'Update the data source for a chart (Excel only).',
    inputSchema: z.object({
      chartName: z.string().describe('Name of the chart'),
      dataRange: z.string().describe('New data range address (e.g., "A1:D10")'),
      hasHeaders: z
        .boolean()
        .optional()
        .default(true)
        .describe('First row contains headers'),
      seriesBy: z
        .enum(['Auto', 'Columns', 'Rows'])
        .optional()
        .default('Auto')
        .describe('How data series are organized'),
    }),
  },

  setChartPosition: {
    title: 'Setting chart position',
    description: 'Set the position of a chart on a worksheet (Excel only).',
    inputSchema: z.object({
      chartName: z.string().describe('Name of the chart'),
      startCell: z
        .string()
        .describe('Top-left cell address for chart position (e.g., "F1")'),
      endCell: z
        .string()
        .optional()
        .describe('Bottom-right cell address (optional, for sizing)'),
    }),
  },

  listCharts: {
    title: 'Listing charts',
    description: 'List all charts in the active worksheet (Excel only).',
    inputSchema: z.object({
      sheetName: z
        .string()
        .optional()
        .describe('Sheet name (if omitted, uses active sheet)'),
    }),
  },

  // Range operations
  moveRange: {
    title: 'Moving range',
    description: 'Move a range to a new location.',
    inputSchema: z.object({
      sourceAddress: z
        .string()
        .describe('Source range address (e.g., "A1:C10")'),
      targetAddress: z
        .string()
        .describe('Target cell address (top-left of destination, e.g., "E1")'),
    }),
  },

  selectRange: {
    title: 'Selecting range',
    description: 'Select a range in the worksheet.',
    inputSchema: z.object({
      address: z.string().describe('Range address to select (e.g., "A1:D10")'),
    }),
  },

  groupRange: {
    title: 'Grouping range',
    description: 'Group rows or columns in a range.',
    inputSchema: z.object({
      address: z.string().describe('Range address to group (e.g., "A1:A10")'),
      groupBy: z
        .enum(['ByRows', 'ByColumns'])
        .describe('Group by rows or columns'),
    }),
  },

  ungroupRange: {
    title: 'Ungrouping range',
    description: 'Ungroup rows or columns in a range.',
    inputSchema: z.object({
      address: z.string().describe('Range address to ungroup (e.g., "A1:A10")'),
      groupBy: z
        .enum(['ByRows', 'ByColumns'])
        .describe('Ungroup by rows or columns'),
    }),
  },

  getUsedRange: {
    title: 'Getting used range',
    description: 'Get the used range of a worksheet or range.',
    inputSchema: z.object({
      address: z
        .string()
        .optional()
        .describe(
          'Range address to get used range from. If omitted, gets used range of active sheet.'
        ),
      valuesOnly: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          'Only include cells with values (exclude formatting-only cells)'
        ),
    }),
  },

  // Advanced formatting
  setBorders: {
    title: 'Setting borders',
    description: 'Set borders on one or more ranges (Excel only).',
    inputSchema: z.object({
      addresses: z
        .array(z.string())
        .min(1)
        .max(50)
        .describe(
          'Array of range addresses (e.g., ["A1:D10", "Sheet1!E1:F5"])'
        ),
      borders: z.object({
        top: z.boolean().optional().describe('Top border'),
        bottom: z.boolean().optional().describe('Bottom border'),
        left: z.boolean().optional().describe('Left border'),
        right: z.boolean().optional().describe('Right border'),
        insideVertical: z
          .boolean()
          .optional()
          .describe('Inside vertical borders'),
        insideHorizontal: z
          .boolean()
          .optional()
          .describe('Inside horizontal borders'),
        color: z
          .string()
          .optional()
          .default('#000000')
          .describe('Border color hex (e.g., "#000000")'),
        style: z
          .enum([
            'None',
            'Continuous',
            'Dash',
            'DashDot',
            'DashDotDot',
            'Dot',
            'Double',
            'SlantDashDot',
          ])
          .optional()
          .default('Continuous')
          .describe('Border line style'),
        weight: z
          .enum(['Hairline', 'Thin', 'Medium', 'Thick'])
          .optional()
          .default('Thin')
          .describe('Border weight'),
      }),
    }),
  },

  setColumnWidth: {
    title: 'Setting column width',
    description: 'Set the width of one or more columns.',
    inputSchema: z.object({
      address: z
        .string()
        .describe(
          'Column range address (e.g., "A:A" for column A, "A:C" for columns A-C)'
        ),
      width: z.number().describe('Column width in points'),
    }),
  },

  setRowHeight: {
    title: 'Setting row height',
    description: 'Set the height of one or more rows.',
    inputSchema: z.object({
      address: z
        .string()
        .describe(
          'Row range address (e.g., "1:1" for row 1, "1:5" for rows 1-5)'
        ),
      height: z.number().describe('Row height in points'),
    }),
  },

  autofitColumns: {
    title: 'Auto-fitting columns',
    description: 'Auto-fit column widths to content.',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Column range address (e.g., "A:A" or "A:D")'),
    }),
  },

  autofitRows: {
    title: 'Auto-fitting rows',
    description: 'Auto-fit row heights to content.',
    inputSchema: z.object({
      address: z.string().describe('Row range address (e.g., "1:1" or "1:10")'),
    }),
  },

  // Comment operations
  updateComment: {
    title: 'Updating comment',
    description: 'Update the content of an existing comment (Excel only).',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell address with the comment (e.g., "A1")'),
      content: z.string().describe('New comment text content'),
    }),
  },

  deleteComment: {
    title: 'Deleting comment',
    description: 'Delete a comment from a cell (Excel only).',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell address with the comment (e.g., "A1")'),
    }),
  },

  // Workbook operations
  saveWorkbook: {
    title: 'Saving workbook',
    description: 'Save the workbook (Excel only).',
    inputSchema: z.object({
      saveBehavior: z
        .enum(['Save', 'Prompt'])
        .optional()
        .default('Save')
        .describe('Save behavior: Save immediately or prompt user'),
    }),
  },

  getActiveCell: {
    title: 'Getting active cell',
    description: 'Get the currently active cell.',
    inputSchema: z.object({
      includeValue: z
        .boolean()
        .optional()
        .default(false)
        .describe('Include the value of the active cell'),
    }),
  },

  // Data validation
  getDataValidation: {
    title: 'Getting data validation',
    description: 'Get data validation rules for a range (Excel only).',
    inputSchema: z.object({
      address: z.string().describe('Range address (e.g., "A1:A10")'),
    }),
  },

  clearDataValidation: {
    title: 'Clearing data validation',
    description: 'Clear data validation rules from a range (Excel only).',
    inputSchema: z.object({
      address: z.string().describe('Range address (e.g., "A1:A10")'),
    }),
  },

  // Worksheet operations
  copyWorksheet: {
    title: 'Copying worksheet',
    description: 'Copy a worksheet.',
    inputSchema: z.object({
      sheetName: z.string().describe('Name of the worksheet to copy'),
      newSheetName: z
        .string()
        .optional()
        .describe('Name for the new sheet (if omitted, Excel generates name)'),
      position: z
        .enum(['Before', 'After', 'Beginning', 'End'])
        .optional()
        .describe('Position relative to source sheet'),
    }),
  },

  hideWorksheet: {
    title: 'Hiding worksheet',
    description: 'Hide a worksheet.',
    inputSchema: z.object({
      sheetName: z.string().describe('Name of the worksheet to hide'),
    }),
  },

  showWorksheet: {
    title: 'Showing worksheet',
    description: 'Show a hidden worksheet.',
    inputSchema: z.object({
      sheetName: z.string().describe('Name of the worksheet to show'),
    }),
  },

  // Gmail tools
  listGmailMessages: {
    title: 'Listing Gmail messages',
    description:
      'List Gmail messages from your inbox. Use Gmail search query syntax for filtering (e.g., "is:unread", "from:boss@company.com", "subject:meeting", "after:2024/01/01"). Requires a connected Gmail account.',
    inputSchema: z.object({
      query: z
        .string()
        .optional()
        .describe(
          'Gmail search query (e.g., "is:unread", "from:user@example.com", "subject:invoice after:2024/01/01")'
        ),
      maxResults: z
        .number()
        .optional()
        .default(10)
        .describe('Maximum number of messages to return (1-100, default: 10)'),
    }),
  },

  getGmailMessage: {
    title: 'Reading Gmail message',
    description:
      'Get the full content of a specific Gmail message by its ID. Use listGmailMessages first to get message IDs.',
    inputSchema: z.object({
      messageId: z
        .string()
        .describe('Gmail message ID (from listGmailMessages)'),
    }),
  },

  searchGmailMessages: {
    title: 'Searching Gmail',
    description:
      'Search Gmail messages using Gmail query syntax. More powerful than listGmailMessages for complex searches. Examples: "from:boss is:unread", "subject:meeting after:2024/01/01", "has:attachment filename:pdf".',
    inputSchema: z.object({
      query: z
        .string()
        .describe(
          'Gmail search query (e.g., "from:boss is:unread", "has:attachment filename:pdf")'
        ),
      maxResults: z
        .number()
        .optional()
        .default(20)
        .describe('Maximum number of messages to return (1-100, default: 20)'),
    }),
  },

  listGmailLabels: {
    title: 'Listing Gmail labels',
    description:
      'List all Gmail labels (INBOX, SENT, custom labels, etc.). Useful to discover available labels for filtering.',
    inputSchema: z.object({}),
  },

  sendGmailMessage: {
    title: 'Sending Gmail message',
    description:
      'Send an email via the connected Gmail account. Requires the Gmail connector to have "Read & Send" permission. Supports plain text body, CC, BCC, and replying to existing messages.',
    inputSchema: z.object({
      to: z.string().describe('Recipient email address'),
      subject: z.string().describe('Email subject'),
      body: z.string().describe('Email body (plain text)'),
      cc: z
        .string()
        .optional()
        .describe('CC recipients (comma-separated email addresses)'),
      bcc: z
        .string()
        .optional()
        .describe('BCC recipients (comma-separated email addresses)'),
      replyToMessageId: z
        .string()
        .optional()
        .describe(
          'Gmail message ID to reply to (for threading). Get from listGmailMessages or searchGmailMessages.'
        ),
    }),
  },

  createLinkedInPost: {
    title: 'Creating LinkedIn post',
    description:
      'Create a text post on LinkedIn on behalf of the user. Requires a connected LinkedIn account with "Profile + Post" permission.',
    inputSchema: z.object({
      text: z
        .string()
        .max(3000)
        .describe('Post content text (max 3000 characters)'),
    }),
  },

  // Google Calendar tools
  listCalendars: {
    title: 'Listing calendars',
    description:
      'List all Google calendars the user has access to. Returns calendar IDs and names.',
    inputSchema: z.object({}),
  },

  listCalendarEvents: {
    title: 'Listing calendar events',
    description:
      'List events from a Google Calendar. Supports filtering by time range.',
    inputSchema: z.object({
      calendarId: z
        .string()
        .optional()
        .default('primary')
        .describe('Calendar ID (default: "primary" for main calendar)'),
      timeMin: z
        .string()
        .optional()
        .describe(
          'Start of time range (ISO 8601 format, e.g., "2024-01-01T00:00:00Z")'
        ),
      timeMax: z
        .string()
        .optional()
        .describe(
          'End of time range (ISO 8601 format, e.g., "2024-12-31T23:59:59Z")'
        ),
      maxResults: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of events to return (1-250, default: 25)'),
    }),
  },

  getCalendarEvent: {
    title: 'Reading calendar event',
    description:
      'Get the full details of a specific Google Calendar event by its ID.',
    inputSchema: z.object({
      calendarId: z
        .string()
        .optional()
        .default('primary')
        .describe('Calendar ID (default: "primary")'),
      eventId: z.string().describe('Event ID'),
    }),
  },

  createCalendarEvent: {
    title: 'Creating calendar event',
    description:
      'Create a new event on a Google Calendar. Requires "Read & Write" permission.',
    inputSchema: z.object({
      calendarId: z
        .string()
        .optional()
        .default('primary')
        .describe('Calendar ID (default: "primary")'),
      summary: z.string().describe('Event title'),
      start: z
        .string()
        .describe('Start time (ISO 8601, e.g., "2024-03-15T10:00:00-05:00")'),
      end: z
        .string()
        .describe('End time (ISO 8601, e.g., "2024-03-15T11:00:00-05:00")'),
      description: z.string().optional().describe('Event description'),
      location: z.string().optional().describe('Event location'),
      attendees: z
        .array(z.string())
        .optional()
        .describe('List of attendee email addresses'),
    }),
  },

  updateCalendarEvent: {
    title: 'Updating calendar event',
    description:
      'Update an existing Google Calendar event. Requires "Read & Write" permission.',
    inputSchema: z.object({
      calendarId: z
        .string()
        .optional()
        .default('primary')
        .describe('Calendar ID (default: "primary")'),
      eventId: z.string().describe('Event ID to update'),
      summary: z.string().optional().describe('New event title'),
      start: z.string().optional().describe('New start time (ISO 8601)'),
      end: z.string().optional().describe('New end time (ISO 8601)'),
      description: z.string().optional().describe('New event description'),
      location: z.string().optional().describe('New event location'),
    }),
  },

  deleteCalendarEvent: {
    title: 'Deleting calendar event',
    description:
      'Delete an event from a Google Calendar. Requires "Read & Write" permission.',
    inputSchema: z.object({
      calendarId: z
        .string()
        .optional()
        .default('primary')
        .describe('Calendar ID (default: "primary")'),
      eventId: z.string().describe('Event ID to delete'),
    }),
  },

  // Google Drive tools
  listDriveFiles: {
    title: 'Listing Drive files',
    description:
      'List files and folders from Google Drive. Supports filtering by MIME type.',
    inputSchema: z.object({
      query: z
        .string()
        .optional()
        .describe(
          'Google Drive search query (e.g., "name contains \'report\'")'
        ),
      maxResults: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of files to return (1-100, default: 25)'),
      mimeType: z
        .string()
        .optional()
        .describe(
          'Filter by MIME type (e.g., "application/pdf", "application/vnd.google-apps.spreadsheet")'
        ),
    }),
  },

  getDriveFile: {
    title: 'Reading Drive file metadata',
    description:
      'Get metadata for a specific Google Drive file (name, size, type, sharing info).',
    inputSchema: z.object({
      fileId: z.string().describe('Google Drive file ID'),
    }),
  },

  searchDriveFiles: {
    title: 'Searching Drive files',
    description: 'Search for files in Google Drive by name or content.',
    inputSchema: z.object({
      query: z
        .string()
        .describe('Search query (e.g., "quarterly report", "budget 2024")'),
      maxResults: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of results (1-100, default: 25)'),
    }),
  },

  downloadDriveFileContent: {
    title: 'Downloading Drive file content',
    description:
      'Download the text content of a Google Drive file. Works for Google Docs (exported as text), text files, and CSVs. Not for binary files like images or PDFs.',
    inputSchema: z.object({
      fileId: z.string().describe('Google Drive file ID'),
    }),
  },

  uploadDriveFile: {
    title: 'Uploading file to Drive',
    description:
      'Upload a text file to Google Drive. Requires "Read & Write" permission.',
    inputSchema: z.object({
      name: z.string().describe('File name (e.g., "report.txt", "data.csv")'),
      content: z.string().describe('File content (text)'),
      mimeType: z
        .string()
        .optional()
        .default('text/plain')
        .describe('MIME type (e.g., "text/plain", "text/csv")'),
      parentId: z
        .string()
        .optional()
        .describe('Parent folder ID (if omitted, uploads to root)'),
    }),
  },

  createDriveFolder: {
    title: 'Creating Drive folder',
    description:
      'Create a new folder in Google Drive. Requires "Read & Write" permission.',
    inputSchema: z.object({
      name: z.string().describe('Folder name'),
      parentId: z
        .string()
        .optional()
        .describe('Parent folder ID (if omitted, creates in root)'),
    }),
  },

  // Google Docs tools
  getDocContent: {
    title: 'Reading Google Doc',
    description: 'Get the text content of a Google Docs document by its ID.',
    inputSchema: z.object({
      documentId: z.string().describe('Google Docs document ID'),
    }),
  },

  createDoc: {
    title: 'Creating Google Doc',
    description:
      'Create a new Google Docs document with optional initial content. Requires "Read & Write" permission.',
    inputSchema: z.object({
      title: z.string().describe('Document title'),
      bodyText: z.string().optional().describe('Initial document body text'),
    }),
  },

  appendToDoc: {
    title: 'Appending to Google Doc',
    description:
      'Append text to an existing Google Docs document. Requires "Read & Write" permission.',
    inputSchema: z.object({
      documentId: z.string().describe('Google Docs document ID'),
      text: z.string().describe('Text to append to the document'),
    }),
  },

  // Google Contacts tools
  listContacts: {
    title: 'Listing contacts',
    description: 'List contacts from Google Contacts.',
    inputSchema: z.object({
      maxResults: z
        .number()
        .optional()
        .default(25)
        .describe('Maximum number of contacts to return (1-100, default: 25)'),
    }),
  },

  getContact: {
    title: 'Reading contact',
    description:
      'Get the full details of a specific Google contact by resource name.',
    inputSchema: z.object({
      resourceName: z
        .string()
        .describe('Contact resource name (e.g., "people/c1234567890")'),
    }),
  },

  searchContacts: {
    title: 'Searching contacts',
    description: 'Search for contacts by name, email, or phone number.',
    inputSchema: z.object({
      query: z
        .string()
        .describe('Search query (e.g., "John", "john@example.com")'),
    }),
  },

  createContact: {
    title: 'Creating contact',
    description:
      'Create a new Google contact. Requires "Read & Write" permission.',
    inputSchema: z.object({
      givenName: z.string().describe('First name'),
      familyName: z.string().optional().describe('Last name'),
      email: z.string().optional().describe('Email address'),
      phone: z.string().optional().describe('Phone number'),
    }),
  },

  pasteAttachedCsvToSheet: {
    title: 'Pasting CSV to sheet',
    description:
      'Write an attached CSV file directly to the sheet. Use this when the user attaches a CSV file and wants it pasted/imported into the spreadsheet. The CSV is pre-parsed — you only need to specify where to write it. Much more efficient than manually reading CSV content and calling writeCellRanges.',
    inputSchema: z.object({
      fileIndex: z
        .number()
        .optional()
        .default(0)
        .describe(
          'Index of the attached CSV file (0-based). Usually 0 if only one CSV is attached.'
        ),
      startCell: z
        .string()
        .optional()
        .default('A1')
        .describe(
          'Top-left cell to start writing (e.g., "A1", "Sheet2!B3"). Data expands right and down from this cell.'
        ),
      autofit: z
        .boolean()
        .optional()
        .default(true)
        .describe('Auto-fit columns after writing'),
    }),
  },

  // Cross-platform tools
  deleteNamedRange: {
    title: 'Deleting named range',
    description: 'Delete a named range from the spreadsheet.',
    inputSchema: z.object({
      name: z.string().describe('Name of the named range to delete'),
    }),
  },

  protectSheet: {
    title: 'Protecting sheet',
    description:
      'Protect a sheet to prevent accidental edits. Users can still view the sheet.',
    inputSchema: z.object({
      sheetName: z
        .string()
        .optional()
        .describe(
          'Name of the sheet to protect. If omitted, protects the active sheet.'
        ),
      description: z
        .string()
        .optional()
        .describe('Description for the protection (e.g., "Locked for review")'),
    }),
  },

  unprotectSheet: {
    title: 'Unprotecting sheet',
    description: 'Remove protection from a sheet, allowing edits again.',
    inputSchema: z.object({
      sheetName: z
        .string()
        .optional()
        .describe(
          'Name of the sheet to unprotect. If omitted, unprotects the active sheet.'
        ),
    }),
  },

  // Excel-specific: named ranges, conditional formatting, filters
  createNamedRangeExcel: {
    title: 'Creating named range',
    description: 'Create a named range (Excel only).',
    inputSchema: z.object({
      name: z.string().describe('Name for the range'),
      address: z
        .string()
        .describe('Cell range address (e.g., "A1:B10", "Sheet1!A1:B10")'),
      scope: z
        .enum(['Workbook', 'Worksheet'])
        .optional()
        .default('Workbook')
        .describe('Scope of the named range'),
    }),
  },

  getNamedRangesExcel: {
    title: 'Getting named ranges',
    description: 'Get all named ranges in the workbook (Excel only).',
    inputSchema: z.object({}),
  },

  deleteNamedRangeExcel: {
    title: 'Deleting named range',
    description: 'Delete a named range from the workbook (Excel only).',
    inputSchema: z.object({
      name: z.string().describe('Name of the named range to delete'),
    }),
  },

  setFilterExcel: {
    title: 'Setting filter',
    description:
      'Apply auto-filter to a range. If a filter already exists, it will be replaced. (Excel only)',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell range to filter (e.g., "A1:D10", "Sheet1!A1:D10")'),
    }),
  },

  removeFilterExcel: {
    title: 'Removing filter',
    description: 'Remove auto-filter from a worksheet. (Excel only)',
    inputSchema: z.object({
      sheetName: z
        .string()
        .optional()
        .describe('Sheet name. If omitted, uses active sheet.'),
    }),
  },

  addConditionalFormatRuleExcel: {
    title: 'Adding conditional format rule',
    description:
      'Apply a conditional formatting rule to a range (Excel only). Supports cell value rules, color scales, and custom formula rules.',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell range address (e.g., "A1:C100", "Sheet1!B2:E50")'),
      rule: z.discriminatedUnion('ruleType', [
        z.object({
          ruleType: z.literal('cellValue'),
          operator: z
            .enum([
              'EqualTo',
              'NotEqualTo',
              'GreaterThan',
              'GreaterThanOrEqual',
              'LessThan',
              'LessThanOrEqual',
              'Between',
              'NotBetween',
            ])
            .describe('Comparison operator'),
          formula1: z.string().describe('First value or formula'),
          formula2: z
            .string()
            .optional()
            .describe('Second value (for Between/NotBetween)'),
          format: z.object({
            backgroundColor: z.string().optional().describe('Fill color hex'),
            fontColor: z.string().optional().describe('Font color hex'),
            bold: z.boolean().optional(),
            italic: z.boolean().optional(),
          }),
        }),
        z.object({
          ruleType: z.literal('colorScale'),
          minColor: z.string().describe('Color for minimum value'),
          midColor: z.string().optional().describe('Color for midpoint'),
          maxColor: z.string().describe('Color for maximum value'),
        }),
        z.object({
          ruleType: z.literal('customFormula'),
          formula: z
            .string()
            .describe('Formula returning true/false (e.g., "=MOD(ROW(),2)=0")'),
          format: z.object({
            backgroundColor: z.string().optional(),
            fontColor: z.string().optional(),
            bold: z.boolean().optional(),
            italic: z.boolean().optional(),
          }),
        }),
      ]),
    }),
  },

  clearConditionalFormatRulesExcel: {
    title: 'Clearing conditional format rules',
    description:
      'Remove all conditional formatting rules from a range. (Excel only)',
    inputSchema: z.object({
      address: z
        .string()
        .describe('Cell range address (e.g., "A1:C100", "Sheet1!B2:E50")'),
    }),
  },

  getConditionalFormatRulesExcel: {
    title: 'Getting conditional format rules',
    description:
      'Read all conditional formatting rules for a range. (Excel only)',
    inputSchema: z.object({
      address: z.string().describe('Cell range address (e.g., "A1:C100")'),
    }),
  },

  // =========================================================================
  // MEMORY TOOLS
  // =========================================================================

  searchMemories: {
    title: 'Searching memories',
    description:
      'Search saved memories by keyword. Use when the user asks what you remember or when you need to recall specific information.',
    inputSchema: z.object({
      query: z.string().describe('Search query to find relevant memories'),
      limit: z
        .number()
        .optional()
        .default(10)
        .describe('Maximum number of results to return'),
    }),
  },

  listMemories: {
    title: 'Listing memories',
    description:
      'List all saved memories, optionally filtered by scope. Use when the user wants to see everything you remember.',
    inputSchema: z.object({
      scope: z
        .enum(['global', 'workbook', 'both'])
        .optional()
        .default('both')
        .describe(
          'Filter by scope: global (all workbooks), workbook (current only), or both'
        ),
    }),
  },

  deleteMemory: {
    title: 'Deleting memory',
    description:
      'Delete a saved memory by its ID. Use when the user asks you to forget something.',
    inputSchema: z.object({
      memoryId: z.string().describe('The ID of the memory to delete'),
    }),
  },
} as const
